import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface F103Retencion {
  withholding_code: string;
  withholding_name: string;
  percentage: string;
  base_imponible: string;
  retencion_aplicada: string;
}

interface F103Report {
  header: { ruc: string; razon_social: string; anio: string; mes: string; period_label: string };
  retenciones: F103Retencion[];
  total_retenido: string;
}

const fmt = (cents: string): string => {
  const n = BigInt(cents);
  const sign = n < 0n ? '-' : '';
  const abs = n < 0n ? -n : n;
  return `${sign}${abs / 100n}.${String(abs % 100n).padStart(2, '0')}`;
};

const fmtPct = (pct: string): string => {
  // percentage is × 100,000,000 (e.g., 1_000_000 = 1.00%)
  const n = BigInt(pct);
  return `${(Number(n) / 1_000_000).toFixed(2)}%`;
};

export function F103Page() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['f103', activeOrgId, year, month],
    queryFn: () => api<F103Report>(`/bookkeeping/reports/f103?year=${year}&month=${month}`),
    enabled: !!activeOrgId,
  });

  return (
    <div className="space-y-4 print:space-y-2">
      <div className="flex items-center justify-between print:block">
        <h1 className="text-xl font-semibold">
          {t('reports.f103.title')} — {data?.header.period_label ?? '...'}
        </h1>
        <div className="flex items-center gap-2 print:hidden">
          <input
            type="number"
            value={year}
            min={2020}
            max={2100}
            onChange={(e) => setYear(Number.parseInt(e.target.value, 10))}
            className="w-24 rounded border px-2 py-1 text-sm"
            aria-label={t('reports.f103.year')}
          />
          <select
            value={month}
            onChange={(e) => setMonth(Number.parseInt(e.target.value, 10))}
            className="rounded border px-2 py-1 text-sm"
            aria-label={t('reports.f103.month')}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
              <option key={m} value={m}>
                {String(m).padStart(2, '0')}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => window.print()}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
          >
            {t('reports.f103.print')}
          </button>
        </div>
      </div>

      {isLoading && <p className="text-sm text-slate-500">{t('common:loading')}</p>}
      {error && (
        <p className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {(error as { message?: string }).message ?? 'Error'}
        </p>
      )}

      {data && (
        <>
          <div className="rounded border bg-white p-3 text-sm">
            <span className="text-slate-500">{t('reports.f103.ruc')}: </span>
            <span className="font-mono font-semibold">{data.header.ruc}</span>
            <span className="ml-3 text-slate-500">{t('reports.f103.razon')}: </span>
            <span className="font-semibold">{data.header.razon_social}</span>
          </div>

          <div className="overflow-x-auto rounded border bg-white">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">{t('reports.f103.code')}</th>
                  <th className="px-3 py-2">{t('reports.f103.name')}</th>
                  <th className="px-3 py-2 text-right">{t('reports.f103.percentage')}</th>
                  <th className="px-3 py-2 text-right">{t('reports.f103.base')}</th>
                  <th className="px-3 py-2 text-right">{t('reports.f103.retencion')}</th>
                </tr>
              </thead>
              <tbody>
                {data.retenciones.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-slate-400">
                      {t('reports.f103.empty')}
                    </td>
                  </tr>
                )}
                {data.retenciones.map((r) => (
                  <tr key={r.withholding_code} className="border-b last:border-0">
                    <td className="px-3 py-1.5 font-mono text-xs">{r.withholding_code}</td>
                    <td className="px-3 py-1.5">{r.withholding_name}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtPct(r.percentage)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmt(r.base_imponible)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">
                      {fmt(r.retencion_aplicada)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t bg-slate-50 font-semibold">
                <tr>
                  <td colSpan={4} className="px-3 py-2">
                    {t('reports.f103.total')}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(data.total_retenido)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="text-xs text-slate-500 print:hidden">ⓘ {t('reports.f103.concepto_hint')}</p>
        </>
      )}
    </div>
  );
}
